import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Toolbar,
  Tooltip,
  AppBar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import splLogo from '../img/SPL_Logo.png';
import padelEagleLogo from '../img/padelEagle.png';
import ImageUploadService from '../services/imageUpload';
import PasswordTextField from "../components/PasswordTextField";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface Organization {
  id: string;
  name: string;
  slug: string;
  competitionType: 'league' | 'tournament' | 'both';
  branding: {
    logo?: string;
    heroImage?: string;
    primaryColor: string;
    secondaryColor: string;
    accentColor?: string;
    backgroundColor?: string;
    textColor?: string;
    customDomain?: string;
  };
  subscription: {
    plan: string;
    status: string;
  };
  createdAt: any;
  adminsCount: number;
}

interface OrgAdmin {
  id: string;
  username: string;
  role: string;
  isSuperAdmin?: boolean;
  createdAt: any;
}

interface OrgStats {
  totalPlayers: number;
  totalSeasons: number;
  totalLeagueDays: number;
  totalTournaments: number;
  totalMatches: number;
  leagueDayMatches: number;
  tournamentMatches: number;
}

const SuperAdminDashboard: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [orgStats, setOrgStats] = useState<OrgStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [heroImageFile, setHeroImageFile] = useState<File | null>(null);
  const [heroImagePreview, setHeroImagePreview] = useState<string>('');
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);
  const [adminsDialogOpen, setAdminsDialogOpen] = useState(false);
  const [orgAdmins, setOrgAdmins] = useState<OrgAdmin[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [newAdminUsername, setNewAdminUsername] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [newAdminIsSuperAdmin, setNewAdminIsSuperAdmin] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    slug: string;
    competitionType: 'league' | 'tournament' | 'both';
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    backgroundColor: string;
    textColor: string;
    customDomain: string;
  }>({
    name: '',
    slug: '',
    competitionType: 'league' as 'league' | 'tournament' | 'both',
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    accentColor: '#4caf50',
    backgroundColor: '#ffffff',
    textColor: '#000000',
    customDomain: '',
  });

  useEffect(() => {
    // Check if super admin is logged in
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin/login');
      return;
    }
    fetchOrganizations();
  }, [navigate]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('superAdminToken');
      const response = await fetch(`${API_URL}/api/super-admin/organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          localStorage.removeItem('superAdminToken');
          navigate('/super-admin/login');
          return;
        }
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      setOrganizations(data);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgStats = async (orgId: string) => {
    try {
      setStatsLoading(true);
      const token = localStorage.getItem('superAdminToken');
      const response = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setOrgStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchOrgAdmins = async (orgId: string) => {
    try {
      setAdminsLoading(true);
      const token = localStorage.getItem('superAdminToken');
      const response = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}/admins`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch admins');

      const data = await response.json();
      setOrgAdmins(data);
    } catch (err) {
      console.error('Error fetching org admins:', err);
    } finally {
      setAdminsLoading(false);
    }
  };

  const handleOpenAdmins = async (org: Organization) => {
    setSelectedOrg(org);
    setNewAdminUsername('');
    setNewAdminPassword('');
    setNewAdminIsSuperAdmin(false);
    setAdminsDialogOpen(true);
    await fetchOrgAdmins(org.id);
  };

  const handleCreateAdmin = async () => {
    if (!selectedOrg) return;
    if (!newAdminUsername || !newAdminPassword) {
      setError('Username and password are required');
      return;
    }

    try {
      setCreatingAdmin(true);
      setError('');
      const token = localStorage.getItem('superAdminToken');
      const response = await fetch(`${API_URL}/api/super-admin/organizations/${selectedOrg.id}/admins`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: newAdminUsername,
          password: newAdminPassword,
          isSuperAdmin: newAdminIsSuperAdmin,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create admin');
      }

      setSuccess('Admin created successfully');
      setNewAdminUsername('');
      setNewAdminPassword('');
      setNewAdminIsSuperAdmin(false);
      await fetchOrgAdmins(selectedOrg.id);
      fetchOrganizations();
    } catch (err: any) {
      setError(err.message || 'Failed to create admin');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (adminId: string) => {
    if (!selectedOrg) return;

    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await fetch(`${API_URL}/api/super-admin/organizations/${selectedOrg.id}/admins/${adminId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete admin');
      }

      setSuccess('Admin deleted successfully');
      await fetchOrgAdmins(selectedOrg.id);
      fetchOrganizations();
    } catch (err: any) {
      setError(err.message || 'Failed to delete admin');
    }
  };

  const handleViewOrg = async (org: Organization) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name,
      slug: org.slug,
      competitionType: org.competitionType as 'league' | 'tournament' | 'both',
      primaryColor: org.branding.primaryColor,
      secondaryColor: org.branding.secondaryColor,
      accentColor: org.branding.accentColor || '#4caf50',
      backgroundColor: org.branding.backgroundColor || '#ffffff',
      textColor: org.branding.textColor || '#000000',
      customDomain: org.branding.customDomain || '',
    });
    setLogoPreview(org.branding.logo || '');
    setLogoFile(null);
    setHeroImagePreview(org.branding.heroImage || '');
    setHeroImageFile(null);
    await fetchOrgStats(org.id);
    setEditDialogOpen(true);
  };

  const handleSaveOrg = async () => {
    if (!selectedOrg) return;

    try {
      const token = localStorage.getItem('superAdminToken');
      
      // Upload logo to Firebase Storage if a new file was selected
      let logoUrl = selectedOrg?.branding?.logo || '';
      if (logoFile) {
        setUploadingLogo(true);
        setSuccess('');
        setError('');
        console.log('Uploading logo to Firebase Storage...');
        try {
          logoUrl = await ImageUploadService.uploadOrganizationLogo(logoFile, selectedOrg.id);
          console.log('Logo uploaded successfully:', logoUrl);
        } catch (uploadError: any) {
          setUploadingLogo(false);
          throw new Error(`Logo upload failed: ${uploadError.message}`);
        }
        setUploadingLogo(false);
      }

      // Upload hero banner image to Firebase Storage if a new file was selected
      let heroImageUrl = selectedOrg?.branding?.heroImage || '';
      if (heroImageFile) {
        setUploadingHeroImage(true);
        setSuccess('');
        setError('');
        try {
          heroImageUrl = await ImageUploadService.uploadOrganizationHeroImage(heroImageFile, selectedOrg.id);
        } catch (uploadError: any) {
          setUploadingHeroImage(false);
          throw new Error(`Hero image upload failed: ${uploadError.message}`);
        }
        setUploadingHeroImage(false);
      }

      setLoading(true);
      const response = await fetch(`${API_URL}/api/super-admin/organizations/${selectedOrg.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          competitionType: formData.competitionType,
          branding: {
            logo: logoUrl,
            heroImage: heroImageUrl,
            primaryColor: formData.primaryColor,
            secondaryColor: formData.secondaryColor,
            accentColor: formData.accentColor,
            backgroundColor: formData.backgroundColor,
            textColor: formData.textColor,
            customDomain: formData.customDomain || undefined,
          },
        }),
      });

      if (!response.ok) throw new Error('Failed to update organization');

      setSuccess('Organization updated successfully');
      setEditDialogOpen(false);
      setLogoFile(null);
      setLogoPreview('');
      setHeroImageFile(null);
      setHeroImagePreview('');
      fetchOrganizations();
    } catch (err: any) {
      setError(err.message || 'Failed to update organization');
    } finally {
      setLoading(false);
      setUploadingLogo(false);
      setUploadingHeroImage(false);
    }
  };

  const handleDeleteOrg = async () => {
    if (!selectedOrg || deleteConfirmName !== selectedOrg.name) return;

    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await fetch(`${API_URL}/api/super-admin/organizations/${selectedOrg.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to delete organization');

      setSuccess('Organization deleted successfully');
      setDeleteConfirmOpen(false);
      setDeleteConfirmName('');
      setEditDialogOpen(false);
      fetchOrganizations();
    } catch (err: any) {
      setError(err.message || 'Failed to delete organization');
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleHeroImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setHeroImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setHeroImagePreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateOrg = async () => {
    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await fetch(`${API_URL}/api/super-admin/organizations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          slug: formData.slug,
          competitionType: formData.competitionType,
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
          accentColor: formData.accentColor,
          backgroundColor: formData.backgroundColor,
          textColor: formData.textColor,
        }),
      });

      if (!response.ok) throw new Error('Failed to create organization');

      setSuccess('Organization created successfully');
      setCreateDialogOpen(false);
      setFormData({
        name: '',
        slug: '',
        competitionType: 'league' as 'league' | 'tournament' | 'both',
        primaryColor: '#1976d2',
        secondaryColor: '#dc004e',
        accentColor: '#4caf50',
        backgroundColor: '#ffffff',
        textColor: '#000000',
        customDomain: '',
      });
      setLogoFile(null);
      setLogoPreview('');
      fetchOrganizations();
    } catch (err: any) {
      setError(err.message || 'Failed to create organization');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdminUser');
    navigate('/super-admin/login');
  };

  // Get logo based on organization slug (matches frontend logic)
  const getOrgLogo = (org: Organization | null) => {
    if (!org) return null;
    // Check for custom uploaded logo first
    if (org.branding?.logo) return org.branding.logo;
    // Fall back to hardcoded logos based on slug
    if (org.slug === 'padel-eagle') return padelEagleLogo;
    if (org.slug === 'spl') return splLogo;
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <AdminIcon sx={{ mr: 2, fontSize: 28 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Super Admin Dashboard
          </Typography>
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Organizations</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setFormData({
                name: '',
                slug: '',
                competitionType: 'league' as 'league' | 'tournament' | 'both',
                primaryColor: '#1976d2',
                secondaryColor: '#dc004e',
                accentColor: '#4caf50',
                backgroundColor: '#ffffff',
                textColor: '#000000',
                customDomain: '',
              });
              setLogoFile(null);
              setLogoPreview('');
              setCreateDialogOpen(true);
            }}
          >
            Create Organization
          </Button>
        </Box>

        {organizations.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="textSecondary">
                No organizations created yet
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Admins</TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organizations.map((org) => (
                  <TableRow key={org.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: org.branding.primaryColor,
                          }}
                        />
                        {org.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={org.slug} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{org.competitionType}</TableCell>
                    <TableCell>
                      <Chip
                        label={org.subscription.plan}
                        size="small"
                        color={org.subscription.plan === 'free' ? 'default' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>{org.adminsCount}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: 'textSecondary' }}>
                      {org.branding.customDomain || '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Manage Admins">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenAdmins(org)}
                        >
                          <PeopleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleViewOrg(org)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedOrg(org);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Edit Organization Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Organization - {selectedOrg?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '70vh', overflow: 'auto' }}>
          <TextField
            label="Name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <FormControl fullWidth>
            <InputLabel>Competition Type</InputLabel>
            <Select
              value={formData.competitionType}
              label="Competition Type"
              onChange={(e) => setFormData({ ...formData, competitionType: e.target.value as any })}
            >
              <MenuItem value="league">League</MenuItem>
              <MenuItem value="tournament">Tournament</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Logo</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {(() => {
                const currentLogo = logoPreview || getOrgLogo(selectedOrg);
                const isCustomLogo = selectedOrg?.branding?.logo;
                const isHardcodedLogo = !isCustomLogo && currentLogo;
                
                return currentLogo ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      component="img"
                      src={currentLogo}
                      alt="Organization logo"
                      onError={(e: any) => {
                        console.warn('Failed to load logo:', currentLogo);
                      }}
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        objectFit: 'contain', 
                        border: '2px solid #ddd', 
                        borderRadius: 1,
                        backgroundColor: '#f5f5f5',
                        p: 0.5
                      }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {isHardcodedLogo ? 'Default Logo (hardcoded)' : 'Current Logo'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px dashed #ddd', 
                        borderRadius: 1,
                        backgroundColor: '#fafafa',
                        color: '#999'
                      }}
                    >
                      No Logo
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      No logo set
                    </Typography>
                  </Box>
                );
              })()}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  component="label"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {getOrgLogo(selectedOrg) ? 'Upload Custom Logo' : 'Upload Logo'}
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleLogoChange}
                  />
                </Button>
                {logoFile && (
                  <Typography variant="caption" color="primary">
                    New logo selected
                  </Typography>
                )}
                {uploadingLogo && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="primary">
                      Uploading logo...
                    </Typography>
                  </Box>
                )}
                {selectedOrg && !selectedOrg.branding?.logo && getOrgLogo(selectedOrg) && (
                  <Typography variant="caption" color="textSecondary" sx={{ maxWidth: 200 }}>
                    💡 Currently using default logo. Upload a custom one to override.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Hero Banner Image</Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
              Optional. If set, this fully replaces the public homepage's gradient hero with your own
              pre-designed banner image (club name/logo baked into the image). Wide format recommended
              (e.g. 1920×640).
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {heroImagePreview ? (
                <Box
                  component="img"
                  src={heroImagePreview}
                  alt="Hero banner"
                  sx={{
                    width: 200,
                    height: 67,
                    objectFit: 'cover',
                    border: '2px solid #ddd',
                    borderRadius: 1,
                    backgroundColor: '#f5f5f5',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 200,
                    height: 67,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed #ddd',
                    borderRadius: 1,
                    backgroundColor: '#fafafa',
                    color: '#999',
                  }}
                >
                  No banner set
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="outlined" component="label" sx={{ whiteSpace: 'nowrap' }}>
                  {heroImagePreview ? 'Replace Banner' : 'Upload Banner'}
                  <input hidden accept="image/*" type="file" onChange={handleHeroImageChange} />
                </Button>
                {heroImageFile && (
                  <Typography variant="caption" color="primary">
                    New banner selected
                  </Typography>
                )}
                {uploadingHeroImage && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="primary">
                      Uploading banner...
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Typography variant="subtitle2" gutterBottom>Branding Colors</Typography>

          <TextField
            label="Primary Color"
            type="color"
            value={formData.primaryColor}
            onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
            inputProps={{ style: { height: 50 } }}
          />

          <TextField
            label="Secondary Color"
            type="color"
            value={formData.secondaryColor}
            onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
            inputProps={{ style: { height: 50 } }}
          />

          <TextField
            label="Accent Color"
            type="color"
            value={formData.accentColor}
            onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
            inputProps={{ style: { height: 50 } }}
          />

          <TextField
            label="Background Color"
            type="color"
            value={formData.backgroundColor}
            onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
            inputProps={{ style: { height: 50 } }}
          />

          <TextField
            label="Text Color"
            type="color"
            value={formData.textColor}
            onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
            inputProps={{ style: { height: 50 } }}
          />

          <TextField
            label="Custom Domain"
            placeholder="e.g., padel-club.com"
            fullWidth
            value={formData.customDomain}
            onChange={(e) => setFormData({ ...formData, customDomain: e.target.value })}
          />

          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : orgStats ? (
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle2" gutterBottom>
                Organization Stats
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Players
                  </Typography>
                  <Typography variant="h6">{orgStats.totalPlayers}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Matches
                  </Typography>
                  <Typography variant="h6">{orgStats.totalMatches}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Seasons
                  </Typography>
                  <Typography variant="h6">{orgStats.totalSeasons}</Typography>
                </Grid>
                {selectedOrg?.competitionType === 'league' && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      League Days
                    </Typography>
                    <Typography variant="h6">{orgStats.totalLeagueDays}</Typography>
                  </Grid>
                )}
                {selectedOrg?.competitionType === 'tournament' && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Tournaments
                    </Typography>
                    <Typography variant="h6">{orgStats.totalTournaments}</Typography>
                  </Grid>
                )}
                {selectedOrg?.competitionType === 'both' && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        League Days
                      </Typography>
                      <Typography variant="h6">{orgStats.totalLeagueDays}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Tournaments
                      </Typography>
                      <Typography variant="h6">{orgStats.totalTournaments}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(true);
            }}
            color="error"
          >
            Delete Organization
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setEditDialogOpen(false)} disabled={uploadingLogo || loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveOrg} 
            variant="contained"
            disabled={uploadingLogo || loading}
            startIcon={uploadingLogo || loading ? <CircularProgress size={20} /> : undefined}
          >
            {uploadingLogo ? 'Uploading Logo...' : loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Organization Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Organization</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Organization Name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Berlin Padel Club"
          />

          <TextField
            label="Slug (URL-friendly)"
            fullWidth
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            placeholder="e.g., berlin-padel-club"
            helperText="Used in URLs and for domain routing"
          />

          <FormControl fullWidth>
            <InputLabel>Competition Type</InputLabel>
            <Select
              value={formData.competitionType}
              label="Competition Type"
              onChange={(e) => setFormData({ ...formData, competitionType: e.target.value as any })}
            >
              <MenuItem value="league">League</MenuItem>
              <MenuItem value="tournament">Tournament</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Primary Color"
            type="color"
            value={formData.primaryColor}
            onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
            inputProps={{ style: { height: 50 } }}
          />

          <TextField
            label="Secondary Color"
            type="color"
            value={formData.secondaryColor}
            onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
            inputProps={{ style: { height: 50 } }}
          />

          <TextField
            label="Accent Color"
            type="color"
            value={formData.accentColor}
            onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
            inputProps={{ style: { height: 50 } }}
          />

          <TextField
            label="Background Color"
            type="color"
            value={formData.backgroundColor}
            onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
            inputProps={{ style: { height: 50 } }}
          />

          <TextField
            label="Text Color"
            type="color"
            value={formData.textColor}
            onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
            inputProps={{ style: { height: 50 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateOrg} variant="contained">
            Create Organization
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Organization?</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography gutterBottom>
            Are you sure you want to delete <strong>{selectedOrg?.name}</strong>? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 2 }}>
            Type the organization name to confirm:
          </Typography>
          <TextField
            fullWidth
            placeholder={selectedOrg?.name}
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteOrg}
            variant="contained"
            color="error"
            disabled={deleteConfirmName !== selectedOrg?.name}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Admins Dialog */}
      <Dialog open={adminsDialogOpen} onClose={() => setAdminsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Admins - {selectedOrg?.name}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {adminsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : orgAdmins.length === 0 ? (
            <Alert severity="info">
              No admin accounts yet. Create one below to enable login to this organization's admin panel.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgAdmins.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.username}</TableCell>
                      <TableCell>
                        {a.isSuperAdmin ? (
                          <Chip label="Super Admin" size="small" color="primary" />
                        ) : (
                          <Chip label="Admin" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteAdmin(a.id)}
                            disabled={orgAdmins.length <= 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Create New Admin</Typography>
          <TextField
            label="Username"
            fullWidth
            value={newAdminUsername}
            onChange={(e) => setNewAdminUsername(e.target.value)}
          />
          <PasswordTextField
            label="Password"
            fullWidth
            value={newAdminPassword}
            onChange={(e) => setNewAdminPassword(e.target.value)}
            helperText="At least 8 characters"
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={newAdminIsSuperAdmin ? 'super' : 'admin'}
              label="Role"
              onChange={(e) => setNewAdminIsSuperAdmin(e.target.value === 'super')}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="super">Super Admin (within this org)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminsDialogOpen(false)}>Close</Button>
          <Button
            onClick={handleCreateAdmin}
            variant="contained"
            disabled={creatingAdmin}
            startIcon={creatingAdmin ? <CircularProgress size={20} /> : undefined}
          >
            {creatingAdmin ? 'Creating...' : 'Create Admin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminDashboard;
